import { CommonModule } from '@angular/common'
import { AfterViewInit, ChangeDetectionStrategy, Component, Inject, Input, OnChanges, OnInit } from '@angular/core'
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { TranslateModule } from '@ngx-translate/core'
import { SharedAuctionFacade } from 'libs/shared/models/auction/auction.models'
import { SharedProfile } from 'libs/shared/models/profile/profile.models'
import * as BasePaddleNumbersActions from 'libs/shared/paddle-numbers/data-access/src/lib/+state/base-paddle-numbers.actions'
import { BasePaddleNumbersFacade } from 'libs/shared/paddle-numbers/data-access/src/lib/+state/base-paddle-numbers.facade'
import { SharedPaddleNumbersDataAccessModule } from 'libs/shared/paddle-numbers/data-access/src/lib/shared-paddle-numbers-data-access.module'
import { SharedUiModule } from 'libs/shared/ui/src/lib/shared-ui.module'
import { GbValidators } from 'libs/shared/utils/helpers/src/lib/gb-validators/gb-validators'
import { Observable, of, take } from 'rxjs'

@UntilDestroy()
@Component({
  selector: 'gb-paddle-number-input',
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    MatIconModule,
    SharedUiModule,
    MatProgressSpinnerModule,
    SharedPaddleNumbersDataAccessModule,
  ],
  templateUrl: './gb-paddle-number-input.component.html',
  styleUrl: './gb-paddle-number-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPaddleNumberInputComponent implements OnInit, OnChanges {
  @Input({ required: true }) control!: FormControl<any>
  @Input() profile?: SharedProfile | null

  requirePaymentMethod$ = this.auctionFacade.paddleNumbersRequirePaymentMethod$
  showLoader$ = of(true)
  initialValue: string | number | null = null
  loaded$ = this.basePaddleNumbersFacade.paddleNumberLoaded$

  constructor(
    private basePaddleNumbersFacade: BasePaddleNumbersFacade,
    @Inject('AuctionFacade') public auctionFacade: SharedAuctionFacade,
  ) {}

  getCalloutMessage(requirePaymentMethod: boolean): string {
    if (!this.profile) {
      return 'SHARED.PADDLE_NUMBER.PROFILE_UNVERIFIED'
    }

    const hasInvitePhone = !!this.profile.invite_phone_number
    const hasInviteEmail = !!this.profile.invite_email
    const usePhoneMessage = hasInvitePhone && !hasInviteEmail

    if (!this.profile?.user && requirePaymentMethod && !this.profile.has_payment_method) {
      return usePhoneMessage
        ? 'SHARED.PADDLE_NUMBER.PROFILE_UNVERIFIED_PHONE_PAYMENT_METHOD'
        : 'SHARED.PADDLE_NUMBER.PROFILE_UNVERIFIED_EMAIL_PAYMENT_METHOD'
    }
    if (!!this.profile?.user && requirePaymentMethod && !this.profile.has_payment_method) {
      return 'SHARED.PADDLE_NUMBER.PROFILE_NO_PAYMENT_METHOD'
    }

    return usePhoneMessage
      ? 'SHARED.PADDLE_NUMBER.PROFILE_UNVERIFIED_PHONE'
      : 'SHARED.PADDLE_NUMBER.PROFILE_UNVERIFIED_EMAIL'
  }

  ngOnInit() {
    this.initialValue = this.control.value
    this.auctionFacade.paddleStartNumber$.pipe(untilDestroyed(this)).subscribe((paddleNumberStart) => {
      this.control.addValidators([Validators.max(9999), Validators.min(paddleNumberStart || 1)])
    })
  }

  ngOnChanges() {
    if (this.control) {
      this.attachValidators()
      this.control.updateValueAndValidity({ emitEvent: false })
    }
  }

  attachValidators() {
    this.control.addAsyncValidators([
      GbValidators.paddleNumberAvailableValidator((value) => this.checkAvailability(value), this.control.value),
    ])
  }

  showPaddleAvailableMessage(): boolean {
    return (
      this.control.value &&
      this.initialValue !== +this.control.value &&
      this.initialValue !== this.control.value &&
      !this.control?.hasError('max') &&
      !this.control?.hasError('min')
    )
  }

  autoSelect() {
    this.basePaddleNumbersFacade.dispatch(
      BasePaddleNumbersActions.getNextAvailablePaddleNumber({
        onSuccess: (paddleNumber: string) => this.autoFillPaddleNumber(paddleNumber),
      }),
    )
  }

  autoFillPaddleNumber(paddleNumber: string) {
    const currentAsyncValidators = this.control.asyncValidator
    this.control.setAsyncValidators(null)

    this.control.setValue(paddleNumber)

    this.control.setAsyncValidators(currentAsyncValidators)
  }

  checkAvailability = (paddleNumber: string): Observable<boolean> => {
    return new Observable<boolean>((observer) => {
      this.basePaddleNumbersFacade.dispatch(
        BasePaddleNumbersActions.checkPaddleNumberAvailability({
          paddleNumber,
          onResponse: (result) => {
            observer.next(result)
            observer.complete()
          },
        }),
      )
    })
  }
}
